import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('PublicProfileDeliveryService', 'users');

/**
 * Delivery state for a public loan-officer profile page.
 *
 * A build REQUEST is not delivery. The only acknowledgment that an officer's
 * page is actually serving their current content is the published manifest
 * of the live Frontfacing deployment:
 *
 *   https://lendwisemtg.com/loan-officers/manifest.json
 *
 * The manifest carries a server-computed `revision` per slug, produced by the
 * same get_public_loan_officer_profile RPC the portal reads. Equal revisions
 * mean the deployed page was built from exactly the content that is live in
 * the database right now.
 */

export const PUBLIC_PROFILE_MANIFEST_URL =
  'https://lendwisemtg.com/loan-officers/manifest.json';

/** Permanent public page for an officer, once delivered. */
export const publicProfilePath = (slug: string) =>
  `https://lendwisemtg.com/loan-officers/${slug}`;

/** Safe fallback that always resolves: the directory with a card anchor. */
export const publicProfileDirectoryAnchor = (slug: string) =>
  `https://lendwisemtg.com/loan-officers#${slug}`;

export type ProfileDeliveryStatus =
  | 'not_published'
  | 'pending_build'
  | 'building'
  | 'live'
  | 'stale'
  | 'build_failed'
  | 'unknown';

export interface ProfileDeliveryState {
  status: ProfileDeliveryStatus;
  /** The URL that is safe to share right now. */
  shareUrl: string;
  /** True only when the permanent per-officer page is confirmed delivered. */
  permanentUrlDelivered: boolean;
  manifestGeneratedAt: string | null;
  lastRequestedAt: string | null;
  indexable: boolean;
}

export interface ManifestOfficer {
  slug: string;
  revision: string | null;
  indexable: boolean;
}

export interface PublicProfileManifest {
  mode: string | null;
  generatedAt: string | null;
  /** Git commit the deployed artifact was built from (40-hex); absent on pre-guard builds. */
  buildSha: string | null;
  /** Deterministic sha256 of the full generated public artifact; the Frontfacing
   *  deploy-skip guard compares it against a fresh build to detect content drift. */
  contentSha: string | null;
  officers: ManifestOfficer[];
}

export interface BuildState {
  lastRequestedAt: string | null;
  lastRequestSource: string | null;
  dirtySince: string | null;
  cooldownSeconds: number;
}

/**
 * How long a requested build may run before an undelivered page is reported
 * as failed rather than in flight. A Frontfacing production build completes
 * in ~2-4 minutes; beyond this window the request did not deliver.
 */
export const BUILD_DELIVERY_TIMEOUT_MS = 20 * 60 * 1000;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const optionalText = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

/** Parse the published manifest, failing closed to null on any drift. */
export function parseManifest(payload: unknown): PublicProfileManifest | null {
  if (!isRecord(payload)) {
return null;
}
  const rawOfficers = payload.officers;
  if (!Array.isArray(rawOfficers)) {
return null;
}

  const officers: ManifestOfficer[] = [];
  for (const entry of rawOfficers) {
    if (!isRecord(entry)) {
return null;
}
    const slug = optionalText(entry.slug);
    if (!slug) {
return null;
}
    officers.push({
      slug,
      revision: optionalText(entry.revision),
      indexable: entry.indexable === true
    });
  }

  return {
    mode: optionalText(payload.mode),
    generatedAt: optionalText(payload.generatedAt),
    buildSha: optionalText(payload.buildSha),
    contentSha: optionalText(payload.contentSha),
    officers
  };
}

export async function fetchPublicProfileManifest(
  fetchImpl: typeof fetch = fetch
): Promise<PublicProfileManifest | null> {
  try {
    const response = await fetchImpl(PUBLIC_PROFILE_MANIFEST_URL, { cache: 'no-store' });
    if (!response.ok) {
return null;
}
    return parseManifest(await response.json());
  } catch (error) {
    log.warn('Public profile manifest fetch failed', { metadata: { error: String(error) } });
    return null;
  }
}

/** The live content revision the deployed page must match to count as live. */
export async function fetchExpectedRevision(slug: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_public_loan_officer_profile', { p_slug: slug });
  if (error || !isRecord(data)) {
return null;
}
  return optionalText(data.revision);
}

export async function fetchBuildState(): Promise<BuildState | null> {
  const { data, error } = await supabase.rpc('get_lo_profile_build_state');
  if (error || !isRecord(data)) {
return null;
}
  return {
    lastRequestedAt: optionalText(data.lastRequestedAt),
    lastRequestSource: optionalText(data.lastRequestSource),
    dirtySince: optionalText(data.dirtySince),
    cooldownSeconds: typeof data.cooldownSeconds === 'number' ? data.cooldownSeconds : 180
  };
}

export async function requestPublicProfileBuild(): Promise<
  { ok: true; dispatched: boolean } | { ok: false; forbidden: boolean }
> {
  const { data, error } = await supabase.rpc('request_lo_profile_build');
  if (error) {
    log.error('Public profile build request failed', { metadata: { code: error.code } }, error);
    return { ok: false, forbidden: error.code === '42501' };
  }
  return { ok: true, dispatched: isRecord(data) && data.dispatched === true };
}

export interface DeliveryInputs {
  published: boolean;
  slug: string | null;
  expectedRevision: string | null;
  manifest: PublicProfileManifest | null;
  buildState: BuildState | null;
  now?: number;
}

/**
 * Pure delivery decision. Every branch is deliberately conservative: the
 * permanent URL is only reported as delivered on a positive revision match,
 * so an unknown or unreadable manifest keeps the directory-anchor fallback.
 */
export function deriveDeliveryState(inputs: DeliveryInputs): ProfileDeliveryState {
  const { published, slug, expectedRevision, manifest, buildState } = inputs;
  const now = inputs.now ?? Date.now();
  const requestedAt = buildState?.lastRequestedAt ? Date.parse(buildState.lastRequestedAt) : NaN;
  const buildInFlight =
    Number.isFinite(requestedAt) && now - requestedAt < BUILD_DELIVERY_TIMEOUT_MS;

  const base = {
    shareUrl: slug ? publicProfileDirectoryAnchor(slug) : '',
    permanentUrlDelivered: false,
    manifestGeneratedAt: manifest?.generatedAt ?? null,
    lastRequestedAt: buildState?.lastRequestedAt ?? null,
    indexable: false
  };

  if (!slug) {
return { ...base, status: 'unknown' };
}
  if (!published) {
return { ...base, status: 'not_published' };
}
  // No readable manifest: never claim or deny delivery.
  if (!manifest) {
return { ...base, status: 'unknown' };
}

  const entry = manifest.officers.find((officer) => officer.slug === slug);

  if (!entry) {
    return {
      ...base,
      status: buildInFlight ? 'building' : requestedAt ? 'build_failed' : 'pending_build'
    };
  }

  // A page exists. Without both revisions we cannot prove it is current.
  if (!entry.revision || !expectedRevision) {
    return { ...base, status: 'unknown', indexable: entry.indexable };
  }

  if (entry.revision === expectedRevision) {
    return {
      ...base,
      status: 'live',
      shareUrl: publicProfilePath(slug),
      permanentUrlDelivered: true,
      indexable: entry.indexable
    };
  }

  // Page exists but was built from older content.
  return {
    ...base,
    status: buildInFlight ? 'building' : requestedAt ? 'build_failed' : 'stale',
    indexable: entry.indexable
  };
}

export const DELIVERY_COPY: Record<ProfileDeliveryStatus, { label: string; detail: string }> = {
  not_published: {
    label: 'Not published',
    detail: 'Publish the profile to generate its public page.'
  },
  pending_build: {
    label: 'Pending build',
    detail: 'A public page has not been generated for this officer yet.'
  },
  building: {
    label: 'Building',
    detail: 'A public site build was requested and has not delivered this page yet.'
  },
  live: {
    label: 'Live',
    detail: 'The public page is serving this officer’s current content.'
  },
  stale: {
    label: 'Stale',
    detail: 'The public page is live but was built from older content.'
  },
  build_failed: {
    label: 'Build failed',
    detail: 'The last requested build did not deliver this page. Request another build.'
  },
  unknown: {
    label: 'Not verified',
    detail: 'The published site manifest could not be read, so delivery is unconfirmed.'
  }
};
